import React, { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { contactInfo } from '../utils/mockData';

/**
 * Floating WhatsApp chat button.
 * - Hidden on /admin routes (managed via the parent in App.js if ever desired).
 * - Shows a small "Chat with us" tooltip pulse on first page load to draw attention.
 * - Stacks above <BackToTop /> on the right edge.
 */
const WhatsAppFloat = () => {
  const [hint, setHint] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Hide the tooltip after the user scrolls or after 6s
  useEffect(() => {
    const timer = setTimeout(() => setHint(false), 6000);
    const onScroll = () => setHint(false);
    window.addEventListener('scroll', onScroll, { passive: true, once: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (dismissed) return null;

  // Build a UK-format E.164 number from the contactInfo whatsapp field
  // Strip any non-digits and prepend "44" if it starts with "0"
  const raw = (contactInfo.whatsapp || '').replace(/\D/g, '');
  const e164 = raw.startsWith('0') ? `44${raw.slice(1)}` : raw;
  const message = "Hi Visual Creationz, I'd like to enquire about your services.";

  // Pick the most reliable URL based on the user's device:
  // - Mobile  → wa.me deep-links into the WhatsApp app
  // - Desktop → web.whatsapp.com goes straight to WhatsApp Web (skips the
  //             api.whatsapp.com redirect, which some networks/firewalls block)
  const buildHref = () => {
    if (typeof navigator === 'undefined') return `https://wa.me/${e164}?text=${encodeURIComponent(message)}`;
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    return isMobile
      ? `https://wa.me/${e164}?text=${encodeURIComponent(message)}`
      : `https://web.whatsapp.com/send?phone=${e164}&text=${encodeURIComponent(message)}`;
  };
  const href = buildHref();

  return (
    <div className="fixed bottom-24 right-4 md:bottom-24 md:right-8 z-40 flex items-end gap-3" data-testid="whatsapp-float-wrapper">
      {/* Tooltip / hint */}
      {hint && (
        <div className="hidden sm:flex items-center gap-2 bg-white text-gray-900 rounded-full pl-4 pr-2 py-2 shadow-2xl animate-bounce-gentle">
          <span className="text-sm font-medium whitespace-nowrap">Chat with us!</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setHint(false); }}
            className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* WhatsApp button */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        data-testid="whatsapp-float-btn"
        className="relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white shadow-2xl shadow-green-500/40 ring-2 ring-white/20 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-300"
      >
        {/* Continuous pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping-slow"></span>
        <MessageCircle size={26} strokeWidth={2.4} className="relative z-10" fill="white" stroke="#25D366" />
      </a>
    </div>
  );
};

export default WhatsAppFloat;
